import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const BASE_URL =
  process.env.REACT_APP_API_URL || 'https://compliance.kurukshetraanalytics.com/backend/api';

const publicApi = axios.create({ baseURL: BASE_URL, timeout: 120000 });

export default function PublicI9Portal() {
  const { token } = useParams();
  const fileRef = useRef();

  const [portalData,   setPortalData]   = useState(null);
  const [loading,      setLoading]      = useState(true);
  const [tokenError,   setTokenError]   = useState('');
  const [submitted,    setSubmitted]    = useState(false);
  const [file,         setFile]         = useState(null);
  const [uploading,    setUploading]    = useState(false);
  const [uploadError,  setUploadError]  = useState('');

  useEffect(() => {
    publicApi.get('/i9/portal.php', { params: { token } })
      .then(res => {
        const d = res.data?.data || res.data;
        setPortalData(d);
        if (d?.employee_section_completed) setSubmitted(true);
      })
      .catch(err => setTokenError(err.response?.data?.message || 'Invalid or expired I-9 link.'))
      .finally(() => setLoading(false));
  }, [token]);

  function handleFile(e) {
    const f = e.target.files?.[0];
    if (!f) return;
    setUploadError('');
    const allowed = ['application/pdf', 'image/jpeg', 'image/png', 'image/jpg'];
    if (!allowed.includes(f.type)) {
      setUploadError('Only PDF, JPG, or PNG files are accepted.');
      return;
    }
    if (f.size > 20 * 1024 * 1024) {
      setUploadError('File is too large. Maximum size is 20 MB.');
      return;
    }
    setFile(f);
  }

  async function handleSubmit() {
    if (!file) { setUploadError('Please select your I-9 document to upload.'); return; }
    setUploading(true); setUploadError('');
    try {
      const fd = new FormData();
      fd.append('token', token);
      fd.append('i9_file', file);
      await publicApi.post('/i9/employee_submit.php', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSubmitted(true);
    } catch (err) {
      if (err.response?.status === 409) { setSubmitted(true); return; }
      setUploadError(err.response?.data?.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  }

  // ── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
          <p className="text-slate-500 text-sm">Loading I-9 portal…</p>
        </div>
      </div>
    );
  }

  // ── Bad token ──────────────────────────────────────────────────────────────
  if (tokenError) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="text-xl font-bold text-slate-800 mb-2">Link Unavailable</h2>
          <p className="text-slate-500 text-sm">{tokenError}</p>
        </div>
      </div>
    );
  }

  // ── Already submitted ──────────────────────────────────────────────────────
  if (submitted) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="bg-white rounded-2xl shadow-lg p-10 max-w-md w-full text-center">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg className="w-8 h-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h2 className="text-2xl font-bold text-slate-800 mb-2">I-9 Document Submitted</h2>
          <p className="text-slate-500 text-sm leading-relaxed">
            Your I-9 document has been received. HR will review it and reach out if anything else is needed.
          </p>
          {portalData?.name && (
            <p className="text-slate-400 text-xs mt-4">{portalData.name}</p>
          )}
        </div>
      </div>
    );
  }

  // ── Upload form ────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="bg-blue-800 text-white py-6 px-4">
        <div className="max-w-xl mx-auto">
          <h1 className="text-2xl font-bold">Form I-9 — Document Upload</h1>
          {portalData?.name && (
            <p className="text-blue-200 text-sm mt-1">
              For <strong className="text-white">{portalData.name}</strong>
            </p>
          )}
        </div>
      </div>

      <div className="max-w-xl mx-auto px-4 py-8 space-y-5">
        {/* Instructions */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800 space-y-2">
          <p className="font-semibold">Instructions</p>
          <ol className="list-decimal list-inside space-y-1 text-blue-700">
            <li>Download the blank I-9 form from <a href="https://www.uscis.gov/i-9" target="_blank" rel="noopener noreferrer" className="underline font-medium">uscis.gov/i-9</a>.</li>
            <li>Complete <strong>Section 1</strong> (Employee Information and Attestation).</li>
            <li>Scan or photograph your completed Section 1 and upload it below.</li>
          </ol>
        </div>

        {/* Upload area */}
        <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4">
          <h2 className="font-semibold text-slate-800">Upload Your I-9 Document</h2>

          {!file ? (
            <div
              onClick={() => fileRef.current?.click()}
              className="border-2 border-dashed border-slate-300 rounded-xl p-10 text-center cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors"
            >
              <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <svg className="w-6 h-6 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                </svg>
              </div>
              <p className="text-sm font-medium text-slate-700">Click to select file</p>
              <p className="text-xs text-slate-400 mt-1">PDF, JPG, or PNG — max 20 MB</p>
            </div>
          ) : (
            <div className="flex items-center gap-3 bg-blue-50 border border-blue-200 rounded-xl px-4 py-3">
              <div className="w-10 h-10 bg-blue-600 rounded-full flex items-center justify-center shrink-0">
                <svg className="w-5 h-5 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-blue-800 truncate">{file.name}</p>
                <p className="text-xs text-blue-500">{(file.size / 1024).toFixed(0)} KB</p>
              </div>
              <button
                onClick={() => { setFile(null); fileRef.current.value = ''; }}
                className="text-xs text-slate-500 hover:text-red-600 border border-slate-200 bg-white px-2 py-1 rounded">
                Remove
              </button>
            </div>
          )}

          <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={handleFile} className="hidden" />

          {uploadError && (
            <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg px-3 py-2 text-sm">
              {uploadError}
            </div>
          )}

          <button
            onClick={handleSubmit}
            disabled={!file || uploading}
            className="w-full bg-blue-600 text-white py-3 rounded-xl text-sm font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {uploading ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Uploading…
              </span>
            ) : 'Submit I-9 Document'}
          </button>
        </div>

        <p className="text-xs text-slate-400 text-center">
          Your document is encrypted and stored securely. Contact HR if you need assistance.
        </p>
      </div>
    </div>
  );
}
